import React from 'react';
import { BarChart, Trash2, Clipboard, UserCircle2 } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных профилей TDEE
 */
const ComparisonSidebar = ({ 
  savedProfiles, 
  deleteSavedProfile, 
  loadSavedProfile 
}) => {
  // Если нет сохраненных профилей
  if (!savedProfiles || savedProfiles.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение профилей
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные профили для сравнения. Нажмите "Сохранить профиль" после расчета TDEE, чтобы сохранить ваши данные.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение профилей
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedProfiles.length} {pluralize(savedProfiles.length, 'профиль', 'профиля', 'профилей')}
        </span>
      </div>

      <div className="space-y-4">
        {savedProfiles.map((profile) => (
          <div 
            key={profile.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <UserCircle2 className="w-4 h-4 mr-1.5 text-emerald-500" />
                {profile.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Clipboard className="w-4 h-4" />}
                  onClick={() => loadSavedProfile(profile.id)}
                  title="Загрузить профиль"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedProfile(profile.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Пол/возраст:</span>
                <span className="font-medium">
                  {profile.formData.gender === 'male' ? 'М' : 'Ж'}, {profile.formData.age} лет
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Параметры:</span>
                <span>
                  {profile.formData.weight}{profile.formData.measurementSystem === 'metric' ? ' кг, ' : ' фунт, '}
                  {profile.formData.height}{profile.formData.measurementSystem === 'metric' ? ' см' : ' дюйм'}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>TDEE:</span>
                <span>
                  {formatNumber(profile.results?.tdee || 0, 0)} ккал
                </span>
              </div>
              <div className="flex justify-between">
                <span>Цель:</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatNumber(profile.results?.targetCalories || 0, 0)} ккал
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;